import { useCallback, useEffect, useMemo, useState } from 'react';

import type { RouteLeg, RoutePlan, RoutePlanStop } from '@/types/route-plan';
import { useRoutePlanStore } from '@/hooks/use-scoped-managers';
import { authenticatedFetch } from '@/lib/services/AuthenticatedFetchService';
import { routeNotificationScheduler } from '@/lib/route-plan/RouteNotificationScheduler';
import { RouteTimelineCalculator } from '@/lib/route-plan/RouteTimelineCalculator';

function todayLocalDate(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/**
 * useActiveRoutePlan — ViewModel for today's active route plan.
 * Powers the home screen tracker, the route map, and the FAB menu label.
 * Manual progression only (no GPS): tap checks a step off, long-press skips.
 */
export function useActiveRoutePlan() {
  const routePlanStore = useRoutePlanStore();
  const [plan, setPlan] = useState<RoutePlan | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!routePlanStore) return;
    try {
      const active = await routePlanStore.getActivePlan(todayLocalDate());
      setPlan(active);
    } catch (error) {
      console.error('[useActiveRoutePlan] Failed to load plan:', error);
      setPlan(null);
    } finally {
      setIsLoading(false);
    }
  }, [routePlanStore]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const completedSet = useMemo(() => new Set(plan?.completedLegIds ?? []), [plan]);

  const progress = useMemo(
    () => (plan ? RouteTimelineCalculator.progress(plan.legs, plan.completedLegIds) : null),
    [plan],
  );

  const currentLegIndex = useMemo(() => {
    if (!plan) return -1;
    return plan.legs.findIndex((leg) => !completedSet.has(leg.id));
  }, [plan, completedSet]);

  const currentLeg: RouteLeg | null =
    plan && currentLegIndex >= 0 ? plan.legs[currentLegIndex] : null;
  const nextLeg: RouteLeg | null =
    plan && currentLegIndex >= 0 ? plan.legs[currentLegIndex + 1] ?? null : null;

  const stopsById = useMemo(
    () => new Map((plan?.stops ?? []).map((stop) => [stop.id, stop])),
    [plan],
  );

  const setTodoTaskCompleted = useCallback(
    async (stop: RoutePlanStop | undefined, completed: boolean) => {
      if (!stop || stop.source !== 'todo' || !stop.sourceId) return;
      try {
        await authenticatedFetch.fetch(`/api/tasks/${stop.sourceId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ completed }),
        });
      } catch (error) {
        // Task may have been deleted meanwhile — the step still toggles.
        console.error('[useActiveRoutePlan] Failed to sync to-do:', error);
      }
    },
    [],
  );

  const markLeg = useCallback(
    async (legId: string, completeLinkedTodo: boolean) => {
      if (!routePlanStore || !plan) return;

      const leg = plan.legs.find((candidate) => candidate.id === legId);
      if (!leg || plan.completedLegIds.includes(legId)) return;

      if (completeLinkedTodo && leg.kind === 'stay') {
        await setTodoTaskCompleted(stopsById.get(leg.stopId), true);
      }
      if (completeLinkedTodo && leg.kind === 'travel') {
        for (const stopId of leg.inlineTodoStopIds ?? []) {
          await setTodoTaskCompleted(stopsById.get(stopId), true);
        }
      }

      const updated = await routePlanStore.updatePlan(plan.date, (current) => {
        const completedLegIds = [...current.completedLegIds, legId];
        const allDone = current.legs.every((candidate) =>
          completedLegIds.includes(candidate.id),
        );
        return {
          ...current,
          completedLegIds,
          status: allDone ? 'completed' : current.status,
        };
      });

      await routeNotificationScheduler.cancelLegNotification(plan.id, legId);
      if (updated?.status === 'completed') {
        await routeNotificationScheduler.cancelAll();
      }
      // Keep the (possibly completed) plan in local state so the step list
      // stays visible after the final check-off; the next refresh() filters
      // completed plans out via getActivePlan.
      setPlan(updated ?? null);
    },
    [routePlanStore, plan, stopsById, setTodoTaskCompleted],
  );

  /** Check the step off (completing a linked to-do too). */
  const checkOffLeg = useCallback((legId: string) => markLeg(legId, true), [markLeg]);

  /** Long-press: skip the step (no to-do side effects). */
  const skipLeg = useCallback((legId: string) => markLeg(legId, false), [markLeg]);

  /** Untick a completed step: reopen linked to-dos, reactivate the plan, reschedule reminders. */
  const untickLeg = useCallback(
    async (legId: string) => {
      if (!routePlanStore || !plan) return;
      const leg = plan.legs.find((candidate) => candidate.id === legId);
      if (!leg || !plan.completedLegIds.includes(legId)) return;

      if (leg.kind === 'stay') {
        await setTodoTaskCompleted(stopsById.get(leg.stopId), false);
      } else {
        for (const stopId of leg.inlineTodoStopIds ?? []) {
          await setTodoTaskCompleted(stopsById.get(stopId), false);
        }
      }

      const updated = await routePlanStore.updatePlan(plan.date, (current) => ({
        ...current,
        completedLegIds: current.completedLegIds.filter((id) => id !== legId),
        status: 'active',
      }));

      if (updated) {
        await routeNotificationScheduler.schedulePlanNotifications(updated);
      }
      setPlan(updated ?? null);
    },
    [routePlanStore, plan, stopsById, setTodoTaskCompleted],
  );

  /** Checkbox tap: tick when unticked, untick when ticked. */
  const toggleLeg = useCallback(
    (legId: string) => {
      if (!plan) return;
      if (plan.completedLegIds.includes(legId)) return untickLeg(legId);
      return markLeg(legId, true);
    },
    [plan, untickLeg, markLeg],
  );

  return {
    plan,
    isLoading,
    progress,
    currentLeg,
    nextLeg,
    currentLegIndex,
    stopsById,
    checkOffLeg,
    skipLeg,
    toggleLeg,
    refresh,
  };
}
